import useOiReferenceLabels from '../../../hooks/useOiReferenceLabels';
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Header from '../../../components/Header';
import Loader from '../../../components/Loader/Loader';
import Activities from '../../../components/Activities/Activity';
import {
  getHVWorkPermitById,
  deleteHVWorkPermit,
  getIsolations,
  getEarths,
  getAppointedOPerators,
  notifyIsolation,
  markIsolation,
  markPermitTakeout,
  notifyIssuePermit,
  markIssuePermit,
  getResponsiblePersons,
  notifyRPChange,
  markRPChange,
  markClearance,
  notifyClearance,
  markAoClearance,
  getIsolationById,
  getEarthById,
  getSignatures,
  getHVWorkPermitDownload,
} from '../../../services/hvWorkPermitService';
import { X, Pencil, Trash2, ChevronDown, ChevronUp } from 'lucide-react';
import ConfirmModal from '../../../components/ConfirmModal/ConfirmModal';
import SuccessModal from '../../../components/ConfirmModal/SuccessModal';
import RejectButton from '../../../components/RejectButton/RejectButton';
import { getStatus, getKeys, getPlantcodes, getLocation } from '../../../services/locationService';
import { getSignatureById } from '../../../services/hvWorkPermitService';
import { useSelector } from 'react-redux';

interface APO {
  id: number;
  name: string;
  avatar?: string | null;
}

type NotificationType = 'isolation' | 'issuePermit' | 'rpChange' | 'clearance';
type ConfirmType = 'delete' | 'isolate' | 'permitTakeout' | 'issuePermit' | 'acceptRpChange' | 'clearance' | 'aoClearance';

const isRequirementEnabled = (value: unknown): boolean => {
  if (typeof value === 'string') {
    return ['yes', 'true', '1', 'checked'].includes(value.trim().toLowerCase());
  }

  return value === true || value === 1;
};

const HVworkPermitDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const role = useSelector((state: any) => state.user?.role);
  const isResponsiblePerson = role === 'responsible_person';
  const [permit, setPermit] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState<{ type: 'success' | 'error'; title?: string; message: string } | null>(null);
  const [notificationModal, setNotificationModal] = useState<NotificationType | null>(null);
  const [confirmModal, setConfirmModal] = useState<ConfirmType | null>(null);
  const [selectedOperator, setSelectedOperator] = useState<APO | null>(null);
  const [candidateList, setCandidateList] = useState<APO[]>([]);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [notifyLoading, setNotifyLoading] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [isolationApproverIds, setIsolationApproverIds] = useState<string[]>([]);
  const [issuePermitApproverIds, setIssuePermitApproverIds] = useState<string[]>([]);
  const [rpChangeApproverIds, setRpChangeApproverIds] = useState<string[]>([]);
  const [apoClearanceApproverIds, setApoClearanceApproverIds] = useState<string[]>([]);

  const [openIsolations, setOpenIsolations] = useState<Record<number, boolean>>({});
  const [openEarths, setOpenEarths] = useState<Record<number, boolean>>({});

  const getOiReferenceLabels = useOiReferenceLabels();
  const [isolations, setIsolations] = useState<any[]>([]);
  const [earths, setEarths] = useState<any[]>([]);
  const [currentUserId, setCurrentUserId] = useState('unknown_user');

  const [statusOptions, setStatusOptions] = useState<any[]>([]);
  const [keyOptions, setKeyOptions] = useState<any[]>([]);
  const [plantCodeOptions, setPlantCodeOptions] = useState<any[]>([]);
  const [locationOptions, setLocationOptions] = useState<any[]>([]);
  const [signatureData, setSignatureData] = useState<any>(null);

  const [downloadLoading, setDownloadLoading] = useState(false);

  const handleDownload = async () => {
    if (!id) return;

    try {
      setDownloadLoading(true);

      const blob = await getHVWorkPermitDownload(id);

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `HVWorkPermit_${id}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      const message = error?.response?.data?.message || error?.response?.data?.error || error?.message || 'Failed to download work permit.';
      setToast({ type: 'error', title: 'Download failed', message });
    } finally {
      setDownloadLoading(false);
    }
  };

  useEffect(() => {
    setCurrentUserId(localStorage.getItem('currentUserID') || '');
  }, []);

  useEffect(() => {
    const getPendingApproverIds = (workflowStages: any[], stageName: string) =>
      (Array.isArray(workflowStages) ? workflowStages : [])
        .filter((stage: any) => stage?.stage === stageName && stage?.status === 'PENDING')
        .flatMap((stage: any) =>
          (Array.isArray(stage?.approvers) ? stage.approvers : [])
            .filter((approver: any) => approver?.status === 'PENDING')
            .map((approver: any) => (approver?.user?.id ?? approver?.user_id ?? approver?.id)?.toString())
            .filter(Boolean),
        );

    setIsolationApproverIds(getPendingApproverIds(permit?.workflow_stages, 'ISOLATION'));

    setIssuePermitApproverIds(getPendingApproverIds(permit?.workflow_stages, 'ISSUE_PERMIT'));

    setRpChangeApproverIds(getPendingApproverIds(permit?.workflow_stages, 'RP_CHANGE'));

    setApoClearanceApproverIds(getPendingApproverIds(permit?.workflow_stages, 'AO_CLEARANCE'));
  }, [permit]);

  const fetchDetails = async () => {
    try {
      const [permitRes] = await Promise.allSettled([getHVWorkPermitById(id)]);

      if (permitRes.status === 'fulfilled') {
        const raw = permitRes.value;
        const data = raw?.data?.hv_work_permit || raw?.data || raw?.hv_work_permit || raw;

        const earthId =
          (typeof data.earth === 'object' ? data.earth?.id : data.earth) ??
          data.earth_id ??
          (typeof data.earths === 'object' ? data.earths?.id : data.earths) ??
          null;

        const signatureId =
          (typeof data.signature === 'object' ? data.signature?.id : data.signature) ??
          data.signature_id ??
          (typeof data.signatures === 'object' ? data.signatures?.id : data.signatures) ??
          null;

        const [isolationsRes, earthResult, sigResult] = await Promise.all([
          getIsolationById(data.isolations).catch((e) => {
            console.log('isolation fetch failed:', e);
            return null;
          }),
          (async () => {
            let earthRecord: any = earthId ? ((await getEarthById(earthId).catch(() => null))?.data ?? null) : null;
            if (!earthRecord) {
              const earthsListRes = await getEarths(id).catch(() => null);
              const earthsList = Array.isArray(earthsListRes?.data)
                ? earthsListRes.data
                : Array.isArray(earthsListRes)
                  ? earthsListRes
                  : [];
              earthRecord =
                earthsList.find((e: any) => e.hv_work_permit_id === Number(id) || e.hv_work_permit?.id === Number(id)) ||
                earthsList[0] ||
                null;
            }
            return earthRecord;
          })(),
          (async () => {
            let sigRecord: any = signatureId ? ((await getSignatureById(signatureId).catch(() => null))?.data ?? null) : null;
            if (!sigRecord) {
              const signaturesListRes = await getSignatures(id).catch(() => null);
              const signaturesList = Array.isArray(signaturesListRes?.data)
                ? signaturesListRes.data
                : Array.isArray(signaturesListRes)
                  ? signaturesListRes
                  : [];
              sigRecord =
                signaturesList.find((s: any) => s.hv_work_permit_id === Number(id) || s.hv_work_permit?.id === Number(id)) ||
                signaturesList[0] ||
                null;
            }
            return sigRecord;
          })(),
        ]);

        if (isolationsRes) {
          const isoRecord = isolationsRes?.data;
          const isolationItems = Array.isArray(isoRecord?.items) ? isoRecord.items : [];
          setIsolations(
            isoRecord
              ? [
                  {
                    id: isoRecord.id,
                    plant_code: isoRecord.plant_code || null,
                    location: isoRecord.location || null,
                    description: isoRecord.describe_components || '',
                    requirements:
                      isRequirementEnabled(isoRecord.requirements) || isolationItems.some((item: any) => isRequirementEnabled(item.req)),
                    items: isolationItems,
                  },
                ]
              : [],
          );
        }

        const earthItems = Array.isArray(earthResult?.items) ? earthResult.items : [];
        setEarths(
          earthResult
            ? [
                {
                  id: earthResult.id,
                  plant_code: earthResult.plant_code || null,
                  location: earthResult.location || null,
                  description: earthResult.describe_apparatus || '',
                  requirements:
                    isRequirementEnabled(earthResult.requirements) || earthItems.some((item: any) => isRequirementEnabled(item.req)),
                  total_earth_required: earthResult.total_earth_required ?? '',
                  total_earth_applied: earthResult.total_earth_applied ?? '',
                  items: earthItems,
                },
              ]
            : [],
        );

        setSignatureData(
          sigResult
            ? {
                name: sigResult.name || '',
                designation: sigResult.designation || sigResult.user_type?.title || sigResult.department?.name || '',
              }
            : null,
        );

        setPermit(data);
      } else {
        throw permitRes.reason;
      }
    } catch (error: any) {
      const message =
        error?.response?.data?.message || error?.response?.data?.error || error?.message || 'Failed to load work permit details';
      setToast({ type: 'error', title: 'Load failed', message });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const loadOptions = async () => {
      try {
        const [statusRes, keysRes, plantCodesRes, locationsRes] = await Promise.all([
          getStatus(),
          getKeys(),
          getPlantcodes(),
          getLocation(),
        ]);
        setStatusOptions(statusRes || []);
        setKeyOptions(keysRes || []);
        setPlantCodeOptions(plantCodesRes || []);
        setLocationOptions(locationsRes || []);
      } catch (err) {
        console.error('Failed to load status/key options', err);
      }
    };
    loadOptions();
  }, []);
  useEffect(() => {
    if (id) fetchDetails();
  }, [id]);
  const canEditPermit = (status: string) => {
    return status?.trim().toLowerCase() === 'new';
  };
  const handleEdit = () => {
    navigate(`/HVworkPermits?edit=${id}`);
  };
  const confirmDelete = async () => {
    if (!id) return;
    try {
      await deleteHVWorkPermit(Number(id));
      setConfirmModal(null);
      setToast({ type: 'success', title: 'Deleted', message: 'Work permit successfully deleted.' });
      setTimeout(() => {
        navigate('/HVworkPermits');
      }, 1200);
    } catch (error: any) {
      const message = error?.response?.data?.message || error?.response?.data?.error || error?.message || 'Failed to delete work permit.';
      setToast({ type: 'error', title: 'Delete failed', message });
    }
  };
  const notificationConfig: Record<
    NotificationType,
    {
      title: string;
      label: string;
      loadApi: () => Promise<any>;
      notifyApi: (permitId: number, payload: any) => Promise<any>;
      successMessage: string;
    }
  > = {
    isolation: {
      title: 'Notify for Isolation',
      label: 'Appointed Operator',
      loadApi: getAppointedOPerators,
      notifyApi: notifyIsolation,
      successMessage: 'Notification successfully sent to the appointed operator for isolation',
    },
    issuePermit: {
      title: 'Notify for Issue Permit',
      label: 'Appointed Operator',
      loadApi: getAppointedOPerators,
      notifyApi: notifyIssuePermit,
      successMessage: 'Notification successfully sent to the appointed operator for issue permit',
    },
    rpChange: {
      title: 'Notify for RP Change',
      label: 'Responsible Person',
      loadApi: getResponsiblePersons,
      notifyApi: notifyRPChange,
      successMessage: 'Notification successfully sent to the responsible person for RP change',
    },
    clearance: {
      title: 'Notify for Clearance',
      label: 'Appointed Operator',
      loadApi: getAppointedOPerators,
      notifyApi: notifyClearance,
      successMessage: 'Notification successfully sent to the appointed operator for clearance',
    },
  };

  const mapUsers = (res: any): APO[] =>
    ((res?.data ?? res ?? []) as any[]).filter(Boolean).map((u: any) => ({
      id: u.id,
      name: u.name || u.full_name || 'Unknown',
    }));

  const loadNotificationCandidates = async (type: NotificationType) => {
    try {
      const res = await notificationConfig[type].loadApi();
      return mapUsers(res);
    } catch (error) {
      console.error(`Failed to load ${type} candidates:`, error);
      return [];
    }
  };

  const openNotificationModal = async (type: NotificationType) => {
    setSelectedOperator(null);
    setIsDropdownOpen(false);
    setCandidateList([]);
    setNotificationModal(type);
    const users = await loadNotificationCandidates(type);
    setCandidateList(users);
  };

  const closeNotificationModal = () => {
    setNotificationModal(null);
    setSelectedOperator(null);
    setIsDropdownOpen(false);
  };

  const handleNotifySubmit = async () => {
    if (!selectedOperator || !id || !notificationModal) return;

    const payload = {
      user_ids: [Number(selectedOperator.id)],
    };

    try {
      setNotifyLoading(true);
      await notificationConfig[notificationModal].notifyApi(Number(id), payload);
      await fetchDetails();
      closeNotificationModal();
      setToast({ type: 'success', title: 'Sent', message: notificationConfig[notificationModal].successMessage });
    } catch (error: any) {
      console.error(`Failed to send ${notificationModal} notification:`, error);
      closeNotificationModal();
      setToast({ type: 'error', title: 'Notification failed', message: 'Failed to send notification. Please try again.' });
    } finally {
      setNotifyLoading(false);
    }
  };

  const handlePermitTakeout = async () => {
    try {
      await markPermitTakeout(id as string);
      await fetchDetails();
      setConfirmModal(null);
    } catch (error: any) {
      console.error('Permit takeout failed:', error);
      setToast({ type: 'error', title: 'Permit Takeout failed', message: 'Failed to mark permit as taken out. Please try again.' });
    }
  };

  const handleConfirmAction = async () => {
    if (!confirmModal || !id) return;

    try {
      setConfirmLoading(true);
      switch (confirmModal) {
        case 'delete':
          await confirmDelete();
          return;
        case 'isolate':
          await markIsolation(id as string);
          await fetchDetails();
          break;
        case 'permitTakeout':
          await markPermitTakeout(id as string);
          await fetchDetails();
          break;
        case 'issuePermit':
          await markIssuePermit(id as string);
          await fetchDetails();
          break;
        case 'acceptRpChange':
          await markRPChange(id as string);
          await fetchDetails();
          break;
        case 'clearance':
          await markClearance(id as string);
          await fetchDetails();
          break;
        case 'aoClearance':
          await markAoClearance(id as string);
          await fetchDetails();
          break;
        default:
          break;
      }
      setToast({ type: 'success', title: 'Success', message: 'Action completed successfully.' });
    } catch (error: any) {
      console.error(`Failed to complete ${confirmModal} action:`, error);
      setToast({ type: 'error', title: 'Action failed', message: 'Unable to complete the selected action. Please try again.' });
    } finally {
      setConfirmLoading(false);
      setConfirmModal(null);
    }
  };

  const toggleIsolation = (index: number) => {
    setOpenIsolations((prev) => ({ ...prev, [index]: !prev[index] }));
  };
  const toggleEarth = (index: number) => {
    setOpenEarths((prev) => ({ ...prev, [index]: !prev[index] }));
  };
  const getStatusColors = (status: string) => {
    const s = status?.toLowerCase() || '';
    if (s === 'new' || s === 'pending') return { text: 'text-purple-600', dot: 'bg-purple-600', badge: 'text-purple-600 bg-purple-50' };
    if (s === 'isolation pending') return { text: 'text-orange-500', dot: 'bg-orange-500', badge: 'text-orange-500 bg-orange-50' };
    if (s === 'permit taken out') return { text: 'text-purple-500', dot: 'bg-purple-500', badge: 'text-purple-500 bg-purple-50' };
    if (s === 'issue permit pending') return { text: 'text-orange-500', dot: 'bg-orange-500', badge: 'text-orange-500 bg-orange-50' };
    if (s === 'permit issued') return { text: 'text-pink-600', dot: 'bg-pink-600', badge: 'text-pink-600 bg-pink-50' };
    if (s === 'rp change pending') return { text: 'text-orange-600', dot: 'bg-orange-600', badge: 'text-orange-600 bg-orange-50' };
    if (s === 'rp changed') return { text: 'text-green-600', dot: 'bg-green-600', badge: 'text-green-600 bg-green-50' };
    if (s === 'clearance done') return { text: 'text-green-500', dot: 'bg-green-500', badge: 'text-green-500 bg-green-50' };
    if (s === 'clearance pending') return { text: 'text-purple-500', dot: 'bg-purple-500', badge: 'text-purple-500 bg-purple-50' };
    if (s === 'isolation effected' || s === 'in progress')
      return { text: 'text-orange-500', dot: 'bg-orange-500', badge: 'text-orange-500 bg-orange-50' };
    if (s === 'clearance done (ao)' || s === 'completed' || s === 'approved')
      return { text: 'text-green-600', dot: 'bg-green-600', badge: 'text-green-600 bg-green-50' };
    return { text: 'text-gray-600', dot: 'bg-gray-600', badge: 'text-gray-600 bg-gray-50' };
  };
  const getIsolationStatusColors = (status: string) => {
    const s = status?.toLowerCase() || '';
    if (s === 'in progress') return { dot: 'bg-orange-500', text: 'text-orange-500' };
    if (s === 'on hold') return { dot: 'bg-yellow-500', text: 'text-yellow-500' };
    if (s === 'completed' || s === 'done') return { dot: 'bg-green-600', text: 'text-green-600' };
    return { dot: 'bg-gray-400', text: 'text-gray-500' };
  };
  // Parse bullet-point text — newlines or array
  const parseTextToList = (value: any): string[] => {
    if (!value) return [];
    if (Array.isArray(value)) return value.filter(Boolean);
    if (typeof value === 'string') {
      return value
        .split('\n')
        .map((s) => s.trim())
        .filter(Boolean);
    }
    return [];
  };

  const getCrossReferenceNames = (permitData: any): string => {
    const refs = permitData?.cross_references;
    if (!Array.isArray(refs) || refs.length === 0) return '-';
    const names = refs.map((ref: any) => ref.reference_number).filter(Boolean);
    return names.length > 0 ? names.join(', ') : '-';
  };

  const confirmMeta: Record<ConfirmType, { title: string; message: string; confirmText: string }> = {
    delete: {
      title: 'Delete work permit',
      message: 'Are you sure you want to delete this work permit?',
      confirmText: 'Yes',
    },
    isolate: {
      title: 'Isolation',
      message: 'Are you sure you want to isolate?',
      confirmText: 'Yes',
    },
    permitTakeout: {
      title: 'Permit take out',
      message: 'Are you sure you want to proceed to take out the permit?',
      confirmText: 'Yes',
    },
    issuePermit: {
      title: 'Issue permit',
      message: 'Are you sure you want to issue the permit?',
      confirmText: 'Yes',
    },
    acceptRpChange: {
      title: 'Accept RP change',
      message: 'Are you sure you want to accept the RP change?',
      confirmText: 'Yes',
    },
    clearance: {
      title: 'Clearance',
      message: 'Are you sure you want to proceed with this work clearance?',
      confirmText: 'Yes',
    },
    aoClearance: {
      title: 'AO clearance',
      message: 'Are you sure you want to proceed with this work clearance?',
      confirmText: 'Yes',
    },
  };

  const activeConfirm = confirmModal ? confirmMeta[confirmModal] : null;
  const activeNotification = notificationModal ? notificationConfig[notificationModal] : null;

  const NotificationModal = () => {
    if (!notificationModal) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
        <div className="bg-white w-[480px] rounded-xl shadow-xl p-6 relative">
          <button onClick={closeNotificationModal} className="absolute top-3 right-3 text-gray-500 hover:text-gray-800">
            <X size={20} />
          </button>
          <h2 className="text-lg font-semibold mb-5">{activeNotification?.title}</h2>
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">Selected {activeNotification?.label}</label>
            <div className="relative">
              <button
                type="button"
                onClick={() => setIsDropdownOpen((prev) => !prev)}
                className="w-full border border-gray-300 rounded-md px-3 py-2.5 text-sm text-gray-700 flex items-center justify-between bg-white focus:outline-none focus:ring-1 focus:ring-blue-500"
              >
                {selectedOperator ? (
                  <span className="flex items-center gap-2">
                    <span className="w-7 h-7 rounded-full bg-gray-300 flex items-center justify-center text-xs font-semibold text-white overflow-hidden flex-shrink-0">
                      {selectedOperator.avatar ? (
                        <img src={selectedOperator.avatar} alt={selectedOperator.name} className="w-full h-full object-cover" />
                      ) : (
                        selectedOperator.name?.charAt(0).toUpperCase()
                      )}
                    </span>
                    <span className="font-medium text-gray-800">{selectedOperator.name},</span>
                    <span className="text-gray-400 text-xs">{activeNotification?.label}</span>
                  </span>
                ) : (
                  <span className="text-gray-400">Select {activeNotification?.label}</span>
                )}
                {isDropdownOpen ? (
                  <ChevronUp size={16} className="text-gray-400 flex-shrink-0" />
                ) : (
                  <ChevronDown size={16} className="text-gray-400 flex-shrink-0" />
                )}
              </button>
              {isDropdownOpen && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-md shadow-lg z-10 max-h-52 overflow-y-auto">
                  {candidateList.length === 0 ? (
                    <div className="px-4 py-3 text-sm text-gray-400">No candidates found</div>
                  ) : (
                    candidateList.map((candidate: APO) => (
                      <button
                        key={candidate.id}
                        type="button"
                        onClick={() => {
                          setSelectedOperator(candidate);
                          setIsDropdownOpen(false);
                        }}
                        className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm text-left hover:bg-blue-50 transition-colors ${selectedOperator?.id === candidate.id ? 'bg-blue-50' : ''}`}
                      >
                        <span className="w-7 h-7 rounded-full bg-gray-300 flex items-center justify-center text-xs font-semibold text-white overflow-hidden flex-shrink-0">
                          {candidate.avatar ? (
                            <img src={candidate.avatar} alt={candidate.name} className="w-full h-full object-cover" />
                          ) : (
                            candidate.name?.charAt(0).toUpperCase()
                          )}
                        </span>
                        <span>
                          <span className="font-medium text-gray-800">{candidate.name},</span>
                          <span className="text-gray-400 text-xs ml-1">{activeNotification?.label}</span>
                        </span>
                      </button>
                    ))
                  )}
                </div>
              )}
            </div>
          </div>
          <div className="flex justify-end gap-3">
            <button
              onClick={closeNotificationModal}
              disabled={notifyLoading}
              className="px-6 py-2 border border-gray-300 rounded-md text-gray-600 hover:bg-gray-100 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
            <button
              onClick={handleNotifySubmit}
              disabled={!selectedOperator || notifyLoading}
              className="px-6 py-2 bg-red-500 text-white rounded-md hover:bg-red-600 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {notifyLoading ? 'Sending...' : 'Done'}
            </button>
          </div>
        </div>
      </div>
    );
  };

  if (loading) return <Loader />;
  if (!permit) return <div className="p-6 text-red-500">No data found</div>;
  const statusColors = getStatusColors(permit.status || 'New');
  const hazardLines = parseTextToList(permit.description || permit.hazards);
  const workLines = parseTextToList(permit.work || permit.work_description);
  return (
    <>
      <Header title="HV Work Permit Details" showBackButton />
      <div className="grid grid-cols-12 min-h-screen">
        {/* ───────────── LEFT CONTENT ───────────── */}
        <div className="col-span-8 px-6 py-6 overflow-y-auto">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <h2 className="font-semibold text-lg">#{permit.ref_no || `WRP${String(permit.id || '').padStart(9, '0')}`}</h2>
              <span className={`flex items-center gap-1.5 text-sm font-medium px-2.5 py-0.5 rounded-full ${statusColors.badge}`}>
                <span className={`w-2 h-2 rounded-full ${statusColors.dot}`}></span>
                {permit.status || 'New'}
              </span>
            </div>
            <div className="flex items-center gap-2">
              {permit.status?.toLowerCase() === 'new' && permit.created_by == currentUserId ? (
                <button
                  onClick={() => openNotificationModal('isolation')}
                  className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white text-sm font-medium rounded-md transition-colors"
                >
                  Notify for Isolation
                </button>
              ) : permit.status?.toLowerCase() === 'isolation pending' && isolationApproverIds.includes(currentUserId) ? (
                <div className="flex items-center justify-end gap-3">
                  <RejectButton title="HV Work Permit" id={id!} apiPath="hv-work-permits" onSuccess={fetchDetails} />
                  <button
                    onClick={() => setConfirmModal('isolate')}
                    className="bg-red-600 hover:bg-red-700 text-white font-medium px-6 py-2 rounded-lg shadow-md transition-all"
                  >
                    Isolation
                  </button>
                </div>
              ) : permit.status?.toLowerCase() === 'isolation effected' && permit.created_by == currentUserId ? (
                <button
                  onClick={() => setConfirmModal('permitTakeout')}
                  className="bg-red-600 hover:bg-red-700 text-white font-medium px-6 py-2.5 rounded-lg shadow-md transition-all"
                >
                  Permit Take Out
                </button>
              ) : permit.status?.toLowerCase() === 'permit taken out' && permit.created_by == currentUserId ? (
                <button
                  onClick={() => openNotificationModal('issuePermit')}
                  className="bg-red-600 hover:bg-red-700 text-white font-medium px-6 py-2.5 rounded-lg shadow-md transition-all"
                >
                  Notify for Issue Permit
                </button>
              ) : permit.status?.toLowerCase() === 'issue permit pending' && issuePermitApproverIds.includes(currentUserId) ? (
                <div className="flex items-center justify-end gap-3">
                  <RejectButton title="HV Work Permit" id={id!} apiPath="hv-work-permits" onSuccess={fetchDetails} />
                  <button
                    onClick={() => setConfirmModal('issuePermit')}
                    className="bg-red-600 hover:bg-red-700 text-white font-medium px-6 py-2 rounded-lg shadow-md transition-all"
                  >
                    Issue Permit
                  </button>
                </div>
              ) : permit.status?.toLowerCase() === 'permit issued' && permit.created_by == currentUserId ? (
                <>
                  <button
                    onClick={() => openNotificationModal('rpChange')}
                    className="border border-red-600 hover:bg-red-600 text-red-600 hover:text-white font-medium px-6 py-2.5 rounded-lg transition-all"
                  >
                    Request RP Change
                  </button>
                  <button
                    onClick={() => setConfirmModal('clearance')}
                    className="bg-red-600 hover:bg-red-700 text-white font-medium px-6 py-2.5 rounded-lg shadow-md transition-all"
                  >
                    Clearance
                  </button>
                </>
              ) : permit.status?.toLowerCase() === 'rp change pending' && rpChangeApproverIds.includes(currentUserId) ? (
                <div className="flex items-center justify-end gap-3">
                  <RejectButton title="HV Work Permit" id={id!} apiPath="hv-work-permits" onSuccess={fetchDetails} />
                  <button
                    onClick={() => setConfirmModal('acceptRpChange')}
                    className="bg-red-600 hover:bg-red-700 text-white font-medium px-6 py-2.5 rounded-lg shadow-md transition-all"
                  >
                    Accept RP Change
                  </button>
                </div>
              ) : permit.status?.toLowerCase() === 'rp changed' && permit.responsible_person == currentUserId ? (
                <button
                  onClick={() => setConfirmModal('clearance')}
                  className="bg-red-600 hover:bg-red-700 text-white font-medium px-6 py-2.5 rounded-lg shadow-md transition-all"
                >
                  Clearance
                </button>
              ) : permit.status?.toLowerCase() === 'clearance done' &&
                (permit.responsible_person ? permit.responsible_person == currentUserId : permit.created_by == currentUserId) ? (
                <button
                  onClick={() => openNotificationModal('clearance')}
                  className="bg-red-600 hover:bg-red-700 text-white font-medium px-6 py-2.5 rounded-lg shadow-md transition-all"
                >
                  Notify for Clearance
                </button>
              ) : permit.status?.toLowerCase() === 'clearance pending' && apoClearanceApproverIds.includes(currentUserId) ? (
                <div className="flex items-center justify-end gap-3">
                  <RejectButton title="HV Work Permit" id={id!} apiPath="hv-work-permits" onSuccess={fetchDetails} />
                  <button
                    onClick={() => setConfirmModal('aoClearance')}
                    className="bg-red-600 hover:bg-red-700 text-white font-medium px-6 py-2 rounded-lg shadow-md transition-all"
                  >
                    Clearance
                  </button>
                </div>
              ) : permit.status?.toLowerCase() === 'clearance done (ao)' &&
                (permit.responsible_person ? permit.responsible_person == currentUserId : permit.created_by == currentUserId) ? (
                <button
                  onClick={handleDownload}
                  disabled={downloadLoading}
                  className="bg-red-600 hover:bg-red-700 text-white font-medium px-6 py-2.5 rounded-lg shadow-md transition-all disabled:opacity-60"
                >
                  {downloadLoading ? 'Downloading...' : 'Download'}
                </button>
              ) : null}
              {isResponsiblePerson && canEditPermit(permit.status || '') && (
                <>
                  <button onClick={() => setConfirmModal('delete')} title="Delete" className="p-2 border rounded-md hover:bg-gray-100">
                    <Trash2 size={16} />
                  </button>
                  <button onClick={handleEdit} title="Edit" className="p-2 border rounded-md hover:bg-gray-100">
                    <Pencil size={16} />
                  </button>
                </>
              )}
            </div>
          </div>

          {/* ── Application Section ── */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-blue-600 mb-4">Application</h3>
            <div className="grid grid-cols-6 gap-4 text-sm">
              <div>
                <p className="text-gray-600 mb-1">Department</p>
                <p className="text-gray-900 font-medium">{permit.department || '-'}</p>
              </div>
              <div>
                <p className="text-gray-600 mb-1">Priority</p>
                <p className="text-gray-900 font-medium">{permit.priority || '-'}</p>
              </div>
              <div>
                <p className="text-gray-600 mb-1">Job Number</p>
                <p className="text-gray-900 font-medium">{permit.job_number || '-'}</p>
              </div>
              <div>
                <p className="text-gray-600 mb-1">EWT</p>
                <p className="text-gray-900 font-medium">{permit.ewt || '-'}</p>
              </div>
              <div>
                <p className="text-gray-600 mb-1">Location</p>
                <p className="text-gray-900 font-medium">{permit.location || '-'}</p>
              </div>
              <div>
                <p className="text-gray-600 mb-1">Cross Reference</p>
                <p className="text-gray-900 font-medium">{getCrossReferenceNames(permit)}</p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-6 gap-4 text-sm mb-6">
            <div>
              <p className="text-gray-600 mb-1">Unit</p>
              <p className="text-gray-900 font-medium">{permit.unit || '-'}</p>
            </div>
            <div>
              <p className="text-gray-600 mb-1">Plant Code</p>
              <p className="text-gray-900 font-medium">{permit.plant_code || '-'}</p>
            </div>
            <div className="col-span-2">
              <p className="text-gray-600 mb-1">Apparatus to be worked On</p>
              <p className="text-gray-900 font-medium">{permit.apparatus || '-'}</p>
            </div>
          </div>

          {/* Hazards Section */}
          <p className="text-gray-600 mb-2 text-sm">Hazards/Precautions/Requests</p>
          <div className="text-gray-900 text-base mb-6 max-w-full">
            {hazardLines.length > 0 ? (
              <ul className="list-disc list-inside space-y-1 break-words">
                {hazardLines.map((hazard, index) => (
                  <li key={index} className="break-words">
                    {hazard}
                  </li>
                ))}
              </ul>
            ) : (
              '-'
            )}
          </div>

          {/* Work to be Carried Out Section */}
          <p className="text-gray-600 mb-2 text-sm">Work to be Carried Out</p>
          <div className="text-gray-900 text-base mb-6 max-w-full">
            {workLines.length > 0 ? (
              <ul className="list-disc list-inside space-y-1 break-words">
                {workLines.map((item, index) => (
                  <li key={index} className="break-words">
                    {item}
                  </li>
                ))}
              </ul>
            ) : (
              '-'
            )}
          </div>

          {/* Isolations/Instructions Section */}
          {isolations.length > 0 && isolations[0].items?.length > 0 && (
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-blue-600 mb-4">Isolations/Instructions</h3>
              <div className="space-y-3">
                {isolations[0].items.map((item: any, index: number) => {
                  const statusLabel = statusOptions.find((s: any) => s.id === Number(item.status))?.title || item.status || 'Pending';
                  const keyLabel = keyOptions.find((k: any) => k.id === Number(item.key))?.title || item.key || 'N/A';
                  const plantCodeLabel =
                    item.plant_code?.title ||
                    plantCodeOptions.find((option: any) => option.id === Number(item.plant_code_id ?? item.plant_code))?.title ||
                    isolations[0].plant_code ||
                    'N/A';
                  const locationLabel =
                    item.location?.title ||
                    locationOptions.find((option: any) => option.id === Number(item.location_id ?? item.location))?.title ||
                    isolations[0].location ||
                    'N/A';
                  const colors = getIsolationStatusColors(statusLabel);

                  return (
                    <div key={item.id ?? index} className="border border-gray-300 rounded-md">
                      <button
                        type="button"
                        onClick={() => toggleIsolation(index)}
                        className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50 transition-colors"
                      >
                        <div className="flex items-center space-x-2">
                          <span className="text-sm font-medium text-gray-900">
                            {index + 1}. {plantCodeLabel}
                          </span>
                          <span className="flex items-center space-x-1.5">
                            <span className={`w-2 h-2 rounded-full ${colors.dot}`}></span>
                            <span className={`text-sm ${colors.text} font-normal`}>{statusLabel}</span>
                          </span>
                        </div>
                        <ChevronDown
                          className={`w-5 h-5 text-gray-600 transition-transform ${openIsolations[index] ? 'transform rotate-180' : ''}`}
                        />
                      </button>

                      {openIsolations[index] && (
                        <div className="px-4 py-3 border-t border-gray-300 bg-gray-50">
                          <div className="space-y-2 text-sm">
                            <p className="text-gray-600 whitespace-pre-wrap break-words">
                              <span className="font-medium">Description:</span>{' '}
                              {item.describe_components ?? item.description ?? isolations[0].description ?? 'N/A'}
                            </p>
                            <p className="text-gray-600 break-words">
                              <span className="font-medium">OPS:</span> {item.ops || 'N/A'}
                            </p>
                            <p className="text-gray-600">
                              <span className="font-medium">Key:</span> {keyLabel}
                            </p>
                            <p className="text-gray-600 break-words">
                              <span className="font-medium">OI Reference:</span> {getOiReferenceLabels(item)}
                            </p>
                            <p className="text-gray-600">
                              <span className="font-medium">Location:</span> {locationLabel}
                            </p>
                            <p className="text-gray-600">
                              <span className="font-medium">Requirements:</span>{' '}
                              {isRequirementEnabled(item.req ?? isolations[0].requirements) ? 'Yes' : 'No'}
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Earths Section */}
          {earths.length > 0 && earths[0].items?.length > 0 && (
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-blue-600 mb-4">Earths</h3>

              <div className="space-y-3">
                {earths[0].items.map((item: any, index: number) => {
                  const itemPlantCode =
                    item.plant_code?.title ||
                    plantCodeOptions.find((option: any) => option.id === Number(item.plant_code_id ?? item.plant_code))?.title ||
                    earths[0].plant_code;
                  const itemLocation =
                    item.location?.title ||
                    locationOptions.find((option: any) => option.id === Number(item.location_id ?? item.location))?.title ||
                    earths[0].location;
                  const statusLabel =
                    statusOptions.find((s: any) => s.id === Number(item.status_id ?? item.status))?.title || item.status || 'Pending';
                  const keyLabel = keyOptions.find((k: any) => k.id === Number(item.key_id ?? item.key))?.title || item.key || 'N/A';
                  const colors = getIsolationStatusColors(statusLabel);

                  return (
                    <div key={item.id ?? index} className="border border-gray-300 rounded-md">
                      <button
                        type="button"
                        onClick={() => toggleEarth(index)}
                        className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50 transition-colors"
                      >
                        <div className="flex items-center space-x-2">
                          <span className="text-sm font-medium text-gray-900">
                            {index + 1}. {itemPlantCode || 'N/A'}
                          </span>
                          <span className="flex items-center space-x-1.5">
                            <span className={`w-2 h-2 rounded-full ${colors.dot}`}></span>
                            <span className={`text-sm ${colors.text} font-normal`}>{statusLabel}</span>
                          </span>
                        </div>
                        <ChevronDown
                          className={`w-5 h-5 text-gray-600 transition-transform ${openEarths[index] ? 'transform rotate-180' : ''}`}
                        />
                      </button>

                      {openEarths[index] && (
                        <div className="px-4 py-3 border-t border-gray-300 bg-gray-50">
                          <div className="space-y-2 text-sm">
                            <p className="text-gray-600 whitespace-pre-wrap break-words">
                              <span className="font-medium">Description:</span> {item.describe_apparatus ?? earths[0].description ?? 'N/A'}
                            </p>
                            <p className="text-gray-600 break-words">
                              <span className="font-medium">OPS:</span> {item.ops || 'N/A'}
                            </p>
                            <p className="text-gray-600">
                              <span className="font-medium">Key:</span> {keyLabel}
                            </p>
                            <p className="text-gray-600">
                              <span className="font-medium">Location:</span> {itemLocation || 'N/A'}
                            </p>
                            <p className="text-gray-600">
                              <span className="font-medium">Requirements:</span>{' '}
                              {isRequirementEnabled(item.req ?? earths[0].requirements) ? 'Yes' : 'No'}
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          {/* Signature Section */}
          {signatureData && (
            <div>
              <h3 className="text-lg font-semibold text-blue-600 mb-4">Signature</h3>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-gray-600 mb-1">Name</p>
                  <p className="text-gray-900 font-medium">{signatureData.name || '-'}</p>
                </div>
                <div>
                  <p className="text-gray-600 mb-1">Role</p>
                  <p className="text-gray-900 font-medium">{signatureData.designation || '-'}</p>
                </div>
              </div>
            </div>
          )}
        </div>
        {/* ── Divider ── */}
        <div className="col-span-1 flex justify-center">
          <div className="w-px bg-gray-200 h-full" />
        </div>
        {/* ── Activities ── */}
        <div className="col-span-3 py-6 pr-6">
          <Activities moduleName="hv_work_permits" moduleItemId={Number(id)} />
        </div>
      </div>
      <NotificationModal />

      <ConfirmModal
        isOpen={confirmModal !== null}
        title={activeConfirm?.title || 'Confirmation'}
        message={activeConfirm?.message || 'Are you sure you want to continue?'}
        confirmText={activeConfirm?.confirmText || 'Yes'}
        cancelText="Cancel"
        onConfirm={handleConfirmAction}
        onCancel={() => setConfirmModal(null)}
        isLoading={confirmLoading}
      />

      {toast && (
        <SuccessModal
          isOpen={!!toast}
          onClose={() => setToast(null)}
          title={toast.title}
          message={toast.message}
          buttonText={undefined}
          type={toast.type}
          autoClose={5000}
        />
      )}
    </>
  );
};
export default HVworkPermitDetails;
